<template>
    <v-col class="register default-slot" md="7" sm="12">
      <div class="top-icons">
        <div class="top-icons">
          <a class="back-icon" v-on:click="changePage(1)">
            <font-awesome-icon icon="long-arrow-alt-left" size="lg" /> Back
          </a>
        </div>
      </div>
      <h1 class="register-title">Dog Information</h1>
      <v-form class="mt-5 mx-10" v-model="isValid">
        <v-row>
          <v-col>
            <v-alert type="info" dismissible>
              <p>Please, verify ALL the information.<br/>If any of the non-editable details are incorrect, contact the ASCA office to adjust puppy's record before proceeding.</p>
            </v-alert>
            <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">
              <pre>{{ validationResult.errors.join("\r\n") }}</pre>
            </v-alert>
            <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined>
              <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
            </v-alert>
          </v-col>
        </v-row>        
        <v-row>
          <v-text-field
            outlined
            label="Official Name *"
            v-model="dogInfo.officialName"
            :rules="[
              (v) => !!v || 'Official Name is required',
              (v) => !(v.split(' ').filter(s => !!s).length < 2) || 'Official Name must have two words',
              (v) => v.length < 50 || '(50) characters max',
            ]"
          >
            <v-tooltip slot="append" dark right>
              <template v-slot:activator="{on, attrs}">
                <font-awesome-icon
                  icon="info-circle"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                />&nbsp;
              </template>
              <span>
                &nbsp;This is your dog's official name. Your dog's name must include at least two words.
              </span>
            </v-tooltip>
          </v-text-field>
          <span><b>&nbsp;FEE: </b> 
              <v-tooltip bottom content-class="fee-table-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"><font-awesome-icon icon="info-circle" /></span>
                </template>
                <dog-registration-fee-table :priceInfo="priceInfo" />
              </v-tooltip>
              <span v-if="!!price">${{ price }}</span>
          </span>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              outlined
              label="Call Name *"
              v-model="dogInfo.callName"
              :rules="[(v) => !!v || 'Call Name is required']"
            />
          </v-col>        
          <v-col>
              <v-text-field
                v-model="dogInfo.tatooChipNumber"
                label="Tattoo/Chip No"
                outlined
              />
            </v-col>
            <v-col>
              <v-checkbox
                v-model="dogInfo.notForBreeding"              
              >
                <template v-slot:label>
                  <span>Not for breeding</span>
                </template>
              </v-checkbox>
            </v-col>
        </v-row>  
        <v-row>
            <v-col>
              <v-select
                v-model="puppyInfo.body"
                label="Body color *"
                :items="dogsLookups.mainColors"
                item-text="description"
                item-value="abbreviation"
                disabled
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                v-model="dogInfo.trimColor"
                label="Trim color *"
                :items="dogsLookups.trimColors"
                item-text="description"
                item-value="abbreviation"
                :rules="[(v) => !!v || 'Trim color is required']"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-select
                v-model="dogInfo.leftEyeColor"
                label="Left eye color *"
                :items="dogsLookups.eyeColors"
                item-text="description"
                item-value="abbreviation"
                :rules="[(v) => !!v || 'Left eye color is required']"
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                v-model="dogInfo.rightEyeColor"
                label="Right eye color *"
                :items="dogsLookups.eyeColors"
                item-text="description"
                item-value="abbreviation"
                :rules="[(v) => !!v || 'Right eye color is required']"
              ></v-select>
            </v-col>
          </v-row>
        <v-row>
          <v-col :lg="10" :md="10" :sm="10">
            <v-row>
              <DatepickerFormatted
                :date="puppyInfo.birthdate"
                label="Date of Birth"
                v-on:updateDates="updateBirthDate($event)"
                :rules="[
                  (v) => !!v || 'Date of birth is required',
                  (v) => new Date(v) < new Date() || 'Date of birth cannot be a future date'
                ]"
                disabled
              />
            </v-row>
            <v-row>
              <v-autocomplete
                v-model="puppyInfo.breed"
                label="Breed"
                :items="dogsLookups.breeds"
                item-text="description"
                item-value="abbreviation"
                :rules="[(v) => !!v || 'Breed is required']"
                disabled
              />
            </v-row>
          </v-col>
          <v-col :lg="2" :md="2" :sm="2">
            <v-radio-group
              v-model="puppyInfo.sex"
              label="Gender"
              :rules="[(v) => !!v || 'Gender is required']"
              disabled
            >
              <v-radio
                key="M"
                label="Male"
                value="M"
              />
              <v-radio
                key="F"
                label="Female"
                value="F"
              />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <h2 class="blue-grey--text text--darken-1">Dam:</h2>
            <v-row class="mt-5">
              <v-col cols="2">
                <v-img
                  :src="defaultImage"
                  class="profile-picture"
                  min-width="40px"
                  max-width="40px"
                  contain
                />
              </v-col>
              <v-col cols="10">
                <h4>
                  <b>{{ puppyInfo.dam.name }}</b>
                </h4>
                <span
                  >{{ puppyInfo.dam.ascaId }} • {{ puppyInfo.dam.mainColor }} •
                  {{ puppyInfo.dam.dnaStatus }}</span
                >
              </v-col>
            </v-row>
            <v-row>
              <p class="ml-3">
                <span v-for="(co, i) in puppyInfo.dam.owners" :key="i">
                  <b>{{ co.isPrimary ? "Primary Owner" : "CoOwner" }}: </b>
                  {{ co.firstName }}
                  {{ co.lastName }}
                  <span v-if="co.memberId">(Member: {{co.memberId}})</span>
                  <br />
                </span>
              </p>
            </v-row>
          </v-col>
          <v-divider vertical light class="d-none d-sm-flex"></v-divider>
          <v-col cols="12" sm="6">
            <h2 class="blue-grey--text text--darken-1">Sire:</h2>
            <v-row class="mt-5">
              <v-col cols="2">
                <v-img
                  :src="defaultImage"
                  class="profile-picture"
                  min-width="40px"
                  max-width="40px"
                  contain
                />
              </v-col>
              <v-col cols="10">
                <h4>
                  <b>{{ puppyInfo.sire.name }}</b>
                </h4>
                <span
                  >{{ puppyInfo.sire.ascaId }} •
                  {{ puppyInfo.sire.mainColor }} •
                  {{ puppyInfo.sire.dnaStatus }}</span
                >
              </v-col>
            </v-row>
            <v-row>
              <p class="ml-3">
                <span v-for="(co, i) in puppyInfo.sire.owners" :key="i">
                  <b>{{ co.isPrimary ? "Primary Owner" : "CoOwner" }}:</b>
                  {{ co.firstName }}
                  {{ co.lastName }}
                  <span v-if="co.memberId">(Member: {{co.memberId}})</span>
                  <br />
                </span>
              </p>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
      <v-btn
        class="white--text"
        large
        color="primary"
        :dark="isValid"
        :disabled="!isValid"
        v-on:click="changePage(3)"
      >NEXT</v-btn>
      <p class="steps">STEP <b>2</b> OF <b>3</b></p>
      <footer-text />
    </v-col>
  </template>

  <script lang="ts">
  import Vue from "vue";
  import lookupsService from "@/services/lookupsService";
  import dogRegistrationService from "@/services/dogRegistrationService";
  import FooterText from "@/components/common/FooterText.vue";
  import DatepickerFormatted from '@/components/common/inputs/DatepickerFormatted.vue';
  import DogRegistrationFeeTable from "@/components/Portal/DogRegistrationFeeTable.vue";
  import { UserInfo } from "@/types/User";
  import { debounce } from "throttle-debounce";
  const defaultImage = require('@/assets/photouploadblue.png');
  export default Vue.extend({
    name: "DogInformation",
    components: { FooterText, DatepickerFormatted, DogRegistrationFeeTable },
    props: {
      changePage: Function,
      dogInfo: Object,
      puppyInfo: Object,
      dogsLookups: Object,
      setDogRegistrationFee: Function,
      price: Number,
      priceInfo: Array,
    },
    created: function() {
      this.reloadData();
    },
    data: function () {
      return {
        defaultImage,
        isValid: false,
        showResults: [] as any[],
        colors: {},
        validationResult: {},
      }
    },
    
  watch: {
    "dogInfo.officialName": {
      handler: debounce(
        2000,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal) return;
          if (newVal) { 
            dogRegistrationService.calculatePuppyRegistrafionFee({requesterPersonId: this.userInfo.personId, puppyId: this.puppyInfo.puppyId, officialName: this.dogInfo.officialName})
            .then(res => {
              this.setDogRegistrationFee(res)}
            );           
            dogRegistrationService.validateDogName(
              this.puppyInfo.puppyId,
              this.dogInfo.officialName
            ).then(validationResult => {
                this.validationResult = validationResult;                    
            });
           }
        }),
      },
    },
    computed: {
      userInfo(): UserInfo {
        return this.$store.state.userInfo;
      },
    },
    methods: {
      reloadData: function() {
        lookupsService.getDogsAttributes().then((res) => {
          this.colors = res;
        });
      },
      updateBirthDate: function (val: any) {
        this.dogInfo.birthDate = val.date;
        //workaround to force rerendering after setting up formatted date
        this.$set(this, "showResults", this.showResults);
      },
      getIsValid() {
        return this.isValid && !!this.dogInfo.breed;
      }
    }
  });
  </script>
